
import {
  FeatureSource,
  Feature,
} from "@models";
import {
  Campaign,
  CharacterFeat,
  CharacterFeatureBase,
  CharacterWeapon,
  CharacterPactBoon,
  CharacterSubrace,
  CharacterSpecialFeature,
  CharacterSpell,
  Spell,
  CharacterBackground,
  Item,
  BasicItem,
  MagicItemKeyword,
  PseudoItem,
  Feat,
  Armor,
  Weapon,
  CharacterClass,
  CharacterSubclass,
  GameClass,
  CharacterGift,
  Gift,
  Background,
  CharacterArmor,
  CharacterItem,
  TrueCreatureInstance,
  Creature,
  CharacterBasicItem,
  Encounter,
  CreatureSpellAsAbility,
  SubclassFeature,
  Character,
  CharacterCustomItem,
  CharacterContainer,
  Ammunition,
  CharacterAmmunition,
  CharacterDeck,
  CharacterGem,
  GemItem,
  CharacterBook,
  BastionFacility,
  CharacterBastionFacility,
  CampaignChar,
  CharacterFeatureSource,
  CreatureInstance,
  CharacterSheetInterface,
  Receivable,
  ReceivableItem,
  CampaignInventory,
  CharacterInventory,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DataUtilities from "./data_utilities";
import CommonUtilities from "@utilities/common_utilities";

const api = API.getInstance();
const data_util = DataUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export class CharConnectUtilitiesClass {
  connectCharacter = async (
    char: Character | CreatureInstance, 
    onProgress?: ((data_type: string, percentage: number ) => void),
    onComplete?: () => Promise<void>
  ) => {
    // debugger;
    if (char instanceof Character) {
      await this.connectCampaign(char, onProgress);
      await this.loadData({ char, onProgress });
      if (char.spirit.sidekick) {
        await this.connectSidekickCreature(char);
      }
      await this.connectStandardFeatures(char, onProgress);
      await this.connectRace(char, onProgress);
      // console.log(char.spirit.race);
      await this.connectGifts(char, onProgress);
      await this.connectConditions(char, onProgress);
      await this.connectBastionFacilities(char, onProgress);
      await this.connectBackground(char, onProgress);
      await this.connectClasses(char, onProgress);
      await this.connectItems(char, onProgress);
      if (char.campaign) {
        await this.connectItems(char.campaign, onProgress);
      }
      await this.connectExtraFeats(char, onProgress);
      // These two need to be done again after char.mind.features is populated
      await this.connectMinions(char, onProgress); 
      await this.connectNotes(char, onProgress);
      await this.connectSpellcastings(char, onProgress);
      char.connected = true;
      if (onProgress) {
        onProgress("Processing", 0);
      }
      if (onComplete) {
        await onComplete(); // The purpose of this is to make it so when it recalculates it will await
      } else {
        char.subscription_holder.recalculate();
      }
    } else {
      debugger;
    }
  }
  loadData = async ({ 
    char,
    onProgress,
    onError
  }: { 
    char: Character | Campaign, 
    onProgress?: ((data_type: string, percentage: number ) => void),
    onError?: ((message: string) => void)
  }) => {
    await data_util.loadData({ char, onProgress, onError });
  }
  connectNotes = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const max = char.char_feature_sources.length;
    let complete = 0;
    const doProgress = () => {
      if (onProgress) {
        onProgress("Notes", complete / max);
      }
    }
    doProgress();
    for (const cfs of char.char_feature_sources) {
      if (cfs.source) {
        if (cfs.source.notes.length > 0) {
          cfs.notes = cfs.source.notes;
        } else if (cfs.source.variant_group.length > 1) {
          const variant_of = data_util.getDominantFromObject(cfs.source);
          if (variant_of instanceof FeatureSource) {
            cfs.notes = variant_of.notes;
          }
        }
      }
      complete++;
      doProgress();
    }
  }
  connectRace = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const doProgress = () => {
      if (onProgress) {
        onProgress("Race", 0);
      }
    }
    doProgress();
    if (!char.spirit.race.source) {
      let fs = data_util.races.find(o => o._id === char.spirit.race.source_id);
      if (!fs) {
        fs = data_util.races.find(o => o.name === char.spirit.race.name && o.isDominant());
        console.log(fs);
      }
      if (!fs && char.spirit.race.source_id !== "") {
        console.error(data_util.races);
        console.error("Race disappearing");
      }
      if (fs) {
        await data_util.loadObject(fs);
        // debugger;
        await char.spirit.race.copy_source({ char, feature_source: fs });
        char.spirit.race.table = await data_util.getTableAsync(fs);
        if (fs.sizes.includes(7)) { // "Size from Subrace"
        } else if (fs.sizes.length > 0 && !fs.sizes.includes(char.spirit.base_size)) {
          char.spirit.base_size = fs.sizes[0];
        }
        const subrace_fb_finder = char.spirit.race.char_feature_bases.filter(o => o.features.filter(o2 => o2.feature_type === "Subrace").length === 1);
        if (subrace_fb_finder.length > 1) {
          for (const scfb of subrace_fb_finder) {
            await this.connectFeatureBase(scfb, char);
          }
        }
        if (subrace_fb_finder.length === 1) {
          const subrace_feature_finder = subrace_fb_finder[0].features.filter(o2 => o2.feature_type === "Subrace");
          if (subrace_feature_finder.length === 1) {
            const subrace_feature = subrace_feature_finder[0];
            if ((subrace_feature.feature_options.length === 0 || 
              subrace_feature.feature_options[0] === null || 
              (subrace_feature.feature_options[0] as CharacterSubrace).source_id === "") && 
              char.spirit.race.old_subrace) {
              subrace_feature.feature_options[0] = char.spirit.race.old_subrace;
            }
            if (subrace_feature.feature_options.length > 0 && 
              subrace_feature.feature_options[0] !== null) {
              const char_subrace = subrace_feature.feature_options[0] as CharacterSubclass;
              const scfs_finder = char.spirit.race.sub_char_feature_sources.filter(o => o.source_id === char_subrace.source_id);
              if (scfs_finder.length === 0) {
                char.spirit.race.sub_char_feature_sources.push(char_subrace);
                char.spirit.race.subrace_id = char_subrace.source_id;
              }
            }
          }
        }
        if (char.spirit.race.subrace_id !== "") {
          const subrace_finder = data_util.subraces.find(o => o._id === char.spirit.race.subrace_id);
      
          if (subrace_finder) {
            const subrace = subrace_finder;
            await data_util.loadObject(subrace);
            if (fs.sizes.includes(7)) { // "Size from Subrace"
              if (subrace.sizes.length > 0 && !subrace.sizes.includes(char.spirit.base_size)) {
                char.spirit.base_size = subrace.sizes[0];
              }
            }
            char.spirit.race.connect_subrace(char, subrace);
          } else {
            char.spirit.race.subrace_id = "";
          }
        }
        for (let i = 0; i < char.spirit.race.all_char_feature_bases.length; ++i) {
          const cfb = char.spirit.race.all_char_feature_bases[i];
          await this.connectFeatureBase(cfb, char);
        }
      } else {
        char.spirit.race.source_id = "";
      }
    }
  }
  connectSidekickCreature = async (char: Character) => {
    if (!char.spirit.sidekick_creature && char.spirit.sidekick_creature_id !== "") {
      const res = await api.getFullObject("creature", char.spirit.sidekick_creature_id);
      if (res instanceof Creature) {
        char.spirit.sidekick_creature = res;
        const ci = new TrueCreatureInstance();
        // await ci.copyCreature(res, res.challenge_rating, true);
        // char.spirit.base_ability_scores = ci.ability_scores;
        // char.spirit.sidekick_ci = ci;
        // for (const condition_id of ci.condition_immunities) {
        //   if (!char.condition_immunities.includes(condition_id)) {
        //     char.condition_immunities.push(condition_id);
        //   }
        // }
      }
    }
  }
  connectGifts = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const remove_us: string[] = [];
    if (char.spirit.gifts.length > 0) {
      const max = char.spirit.gifts.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Gifts", complete / max);
        }
      }
      doProgress();
      const gifts = await data_util.getGifts();
      for (const char_gift of char.spirit.gifts) {
        const gift = gifts.find(o => o._id === char_gift.source_id);
        if (gift) {
          await data_util.loadObject(gift);
          await char_gift.copy_source({ char, feature_source: gift });
          char_gift.table = await data_util.getTableAsync(gift); 
          for (const cfb of char_gift.all_char_feature_bases) {
            await this.connectFeatureBase(cfb, char);
          }
        } else {
          remove_us.push(char_gift.source_id);
        }
        complete++;
        doProgress();
      }
    }
  }
  connectConditions = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const remove_us: string[] = [];
    if (char.spirit.conditions.length > 0) {
      const max = char.spirit.conditions.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Conditions", complete / max);
        }
      }
      doProgress();
      const conditions = await data_util.getConditions();
      for (const char_cond of char.spirit.conditions) {
        const cond = conditions.find(o => o._id === char_cond.source_id);
        if (cond) {
          await data_util.loadObject(cond);
          await char_cond.copy_source({ char, feature_source: cond });
          char_cond.table = await data_util.getTableAsync(cond);
          for (const cfb of char_cond.all_char_feature_bases) {
            await this.connectFeatureBase(cfb, char);
          }
        } else {
          remove_us.push(char_cond.source_id);
        }
        complete++;
        doProgress();
      }
    }
  }
  connectBastionFacilities = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const remove_us: string[] = [];
    if (char.spirit.bastion_facilities.length > 0) {
      const max = char.spirit.bastion_facilities.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Bastion Facilities", complete / max);
        }
      }
      doProgress();
      const bastion_facilities = await data_util.getBastionFacilities();
      for (let k = 0; k < char.spirit.bastion_facilities.length; ++k) {
        const char_bastion_facility = char.spirit.bastion_facilities[k];
        const bastion_facility = bastion_facilities.find(o => o._id === char_bastion_facility.source_id);
        if (bastion_facility) {
          await data_util.loadObject(bastion_facility);
          char_bastion_facility.source = bastion_facility;
        } else {
          remove_us.push(char_bastion_facility.source_id);
        }
        complete++;
        doProgress();
      }
    }
  }
  connectCampaign = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number) => void)) => {
    // debugger;
    if (!char.campaign) {
      let c_char = await api.getCampaignObject<CampaignChar>({ data_type: "campaign_char", filter: { char_id: char._id }});
      if (c_char) {
        const doProgress = (data_type: string, percentage: number) => {
          if (onProgress) {
            onProgress(data_type, percentage);
          }
        }
        doProgress("Campaign", 0);
        const campaign = await api.getCampaign({ campaign_id: c_char.campaign_id, include: ["players","characters"], onProgress });
        if (campaign) {
          const c_char_id = c_char._id;
          c_char = campaign.c_chars.find(o => o._id === c_char_id);
        }
        if (c_char) {
          char.c_char = c_char;
        }
      }
    }
    if (char.campaign) {
      char.c_options.copy(char.campaign.c_options);
    }
  }
  connectStandardFeatures = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (!char.spirit.standard_features.source && char.c_options.standard_features_id !== "") {
      const doProgress = () => {
        if (onProgress) {
          onProgress("Standard Features", 0);
        }
      }
      doProgress();
      const standard_features = data_util.standard_features.find(o => o._id === char.c_options.standard_features_id);
      if (standard_features) {
        await data_util.loadObject(standard_features);
        await char.spirit.standard_features.copy_source({ char, feature_source: standard_features });
      }
    }
  }
  connectBackground = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (!char.spirit.background.source && char.spirit.background.source_id !== "") {
      const doProgress = () => {
        if (onProgress) {
          onProgress("Background", 0);
        }
      }
      doProgress();
      // debugger;
      const obj_finder = data_util.backgrounds.find(o => o._id === char.spirit.background.source_id);
      if (obj_finder) {
        const background = obj_finder;
        await data_util.loadObject(background);
        await char.spirit.background.copy_source({ char, feature_source: background });
        // No Backgrounds currently use Tables
        for (let i = 0; i < char.spirit.background.all_char_feature_bases.length; ++i) {
          await this.connectFeatureBase(char.spirit.background.all_char_feature_bases[i], char);
        }
      } else {
        char.spirit.background.source_id = "";
      }
    }
  }
  connectClasses = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (char.spirit.classes.length > 0) {
      
      const max = char.spirit.classes.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Classes", complete / max);
        }
      }
      doProgress();
      for (let k = 0; k < char.spirit.classes.length; ++k) {
        const char_class = char.spirit.classes[k];
        let fs = data_util.classes.find(o => o._id === char_class.source_id);
        if (!fs) {
          fs = data_util.classes.find(o => o.name === char_class.name && o.isDominant());
        }
        if (fs) {
          const game_class = fs;
          // debugger;
          await data_util.loadObject(game_class);
          char_class.table = await data_util.getTableAsync(game_class);
          await char_class.copy_source({ char, feature_source: game_class });
        }
        const subclass_fb_finder = char_class.char_feature_bases.filter(o => 
          o.features.filter(o2 => 
            o2.floating_feature instanceof SubclassFeature && 
            o2.floating_feature.level === 1).length === 1);
        if (subclass_fb_finder.length > 1) {
          for (const scfb of subclass_fb_finder) {
            await this.connectFeatureBase(scfb, char);
          }
        }
        if (subclass_fb_finder.length === 1) {
          const subclass_feature_finder = subclass_fb_finder[0].features.filter(o2 => o2.feature_type === "Subclass");
          if (subclass_feature_finder.length === 1) {
            const subclass_feature = subclass_feature_finder[0];
            if ((subclass_feature.feature_options.length === 0 || 
              subclass_feature.feature_options[0] === null) && char_class.old_char_subclass) {
              subclass_feature.feature_options[0] = char_class.old_char_subclass;
            }
            if (subclass_feature.feature_options.length > 0 && 
              subclass_feature.feature_options[0] !== null) {
              const char_subclass = subclass_feature.feature_options[0] as CharacterSubclass;
              const scfs_finder = char_class.sub_char_feature_sources.filter(o => o.source_id === char_subclass.source_id);
              if (scfs_finder.length === 0) {
                char_class.sub_char_feature_sources.push(char_subclass);
              }
            }
          }
        }
        const subclass_finder = data_util.subclasses.find(o => o._id === char_class.subclass_id);
        if (subclass_finder) {
          const subclass = subclass_finder;
          await data_util.loadObject(subclass);
          char_class.connect_subclass(char, subclass);
          if (char_class.char_subclass) {
            char_class.char_subclass.table = await data_util.getTableAsync(subclass);
          }
          for (let i = 0; i < char_class.all_char_feature_bases.length; ++i) {
            const cfb = char_class.all_char_feature_bases[i];
            await this.connectFeatureBase(cfb, char);
          }
        } else {
          char_class.sub_char_feature_sources = char_class.sub_char_feature_sources.filter(o => o.source_id !== char_class.subclass_id);
        }
        complete++;
        doProgress();
      }
    }
  }
  connectItems = async (char: Character | Campaign, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    // debugger;
    const inventory = char.inventory;
    if (inventory) {
      const max = inventory.items.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Items", complete / max);
        }
      }
      doProgress();
      for (let k = 0; k < inventory.items.length; ++k) {
        const char_item = inventory.items[k];
        // if (char_item.base_item_id === "600121bbec43802088716283") {
        //   debugger;
        // }
        await this.connectItem(char, char_item);
        complete++;
        doProgress();
      }
      if (inventory instanceof CharacterInventory && inventory.items.filter(o => o.name !== "Equipment" && o.name !== "Storage" && o.name !== "Campaign Storage").length > 0 && 
        !inventory.items.find(o => o.base_item && o.base_item.name === "Backpack")) {
        const backpack = data_util.base_pseudo_items.find(o => o.name === "Backpack");
        if (backpack) {
          // debugger;
          await this.addItem({ char, item: backpack });
        }
      }
      if (char instanceof Character) {
        for (const cbf of char.spirit.bastion_facilities) {
          for (const char_item of cbf.items) {
            await this.connectItem(char, char_item);
          }
          const creatures: Creature[] = [];
          for (const ci of cbf.creatures) {
            await this.connectCreature({ ci, creatures, char });
          }
        }
      }
      // for (const component of char.recipe_components.filter(o => o.creature_details._id !== "")) {
      //   const res = await api.getFullObject("creature", component.creature_details._id);
      //   if (res instanceof Creature) {
      //     component.creature = res;
      //   }
      // }
    }
  }
  connectItem = async (char: Character | Campaign, char_item: CharacterItem) => {
    // if (char_item.name.includes("Scroll")) {
    //   debugger;
    // }
    // debugger;
    if (char_item.base_item_id !== "") {
      let fs = data_util.items.find(o => o._id === char_item.base_item_id);
      if (!fs) {
        fs = data_util.items.find(o => o.name === char_item.name && o.isDominant());
      }
      if (fs) {
        await data_util.loadObject(fs);
        await char_item.connectBaseItem({ owner: char, base_item: fs });
        if (fs instanceof Weapon && 
          char_item instanceof CharacterWeapon && 
          char_item.using_attack_types.length === 0) {
          char_item.using_attack_types = [...fs.the_attack_types];
        }
      } else if (char_item.name !== "Storage" && char_item.name !== "Equipment" && char_item.name !== "Campaign Storage") {
        console.log(data_util.items.length);
        console.log(char_item);
      }
    }
    if (char_item.extra !== "") {
      // debugger;
      let spell = data_util.spells.find(o => o._id === char_item.extra);
      if (!spell) {
        // It's probably archived.  Try to find variants, and take the dominant one.
        spell = data_util.getDominantSpell(char_item.extra);
      }
      if (spell) {
        await data_util.loadObject(spell);
        char_item.extra_spell = spell;
        char_item.extra = spell._id;
      }
    }
    for (const cmik of char_item.char_magic_item_keywords) {
      const magic_item_keyword = data_util.magic_item_keywords.find(o => o._id === cmik.source_id);
      if (magic_item_keyword) {
        await data_util.loadObject(magic_item_keyword);
        await this.connectMagicItemKeyword({
          char, 
          char_item_or_id: char_item, 
          magic_item_keyword_or_id: magic_item_keyword,
          char_feature_id: ""
        });
      }
    }
  }
  connectExtraFeats = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    if (char.spirit.extra_feats.length > 0) {
      const max = char.spirit.extra_feats.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Feats", complete / max);
        }
      }
      doProgress();
      for (let k = 0; k < char.spirit.extra_feats.length; ++k) {
        const char_feat = char.spirit.extra_feats[k];
        if (char_feat.source_id !== "") {
          const feat = data_util.feats.find(o => o._id === char_feat.source_id);
          if (feat) {
            await data_util.loadObject(feat);
            await char_feat.copy_source({ char, feature_source: feat, item_id: char_feat.item_id });
          }
          if (char.spirit.feats.filter(o => o.true_id === char_feat.true_id).length === 0) {
            char.spirit.feats.push(char_feat);
          }
        }
        complete++;
        doProgress();
      }
    }
  }
  connectSpellcastings = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    for (const cfs of char.char_feature_sources) {
      if (cfs.spellcasting) {
        const spellcasting = cfs.spellcasting;
        for (const char_spell of spellcasting.spells) {
          if (!char_spell.spell) {
            const spell = data_util.getSpell(char_spell.spell_id);
            if (spell) {
              char_spell.connectSpell(spell);
            }
          }
        }
        if (spellcasting.spellbook) {
          const spellbook = spellcasting.spellbook;
          for (const char_spell of spellbook.spells) {
            if (!char_spell.spell) {
              const spell = data_util.getSpell(char_spell.spell_id);
              if (spell) {
                char_spell.connectSpell(spell);
              }
            }
          }
        }
      }
    }
  }
  connectMinions = async (char: Character, 
    onProgress?: ((data_type: string, percentage: number ) => void)) => {
    const creatures: Creature[] = [];
    if (char.spirit.minions.length > 0) {
      const max = char.spirit.minions.length;
      let complete = 0;
      const doProgress = () => {
        if (onProgress) {
          onProgress("Minions", complete / max);
        }
      }
      doProgress();
      for (let k = 0; k < char.spirit.minions.length; ++k) {
        const minion = char.spirit.minions[k];
        if (!minion.connected) {
          await this.connectCreature({ ci: minion, creatures, char });
        } 
        complete++;
        doProgress();
      }
    }
    if (char.spirit.transform) {
      const minion = char.spirit.transform;
      if (!minion.connected) {
        await this.connectCreature({ ci: minion, creatures, char });
      } 
    }
  }
  
  connectEncounterCreatures = async (encounter: Encounter) => {
    const creature_participants = encounter.participants.filter(o => o.participant_type === "Creature");
    const creatures: Creature[] = [];
    for (const participant of creature_participants) {
      for (const ci of participant.child_participants) {
        if (ci instanceof TrueCreatureInstance) {
          await this.connectCreature({ci, creatures});
        }
      }
    }
  }
  connectCreature = async ({ ci, creatures, char }: { ci: TrueCreatureInstance, creatures: Creature[], char?: Character }) => {
    if (!api.smart_hash.creature_feature) {
      await api.getObjects({ data_type: "creature_feature" });
    }
    if (!ci.connected && ci.creature_id !== "") {
      let creature = creatures.find(o => o._id === ci.creature_id);
      if (!creature) {
        const res = await api.getFullObject("creature", ci.creature_id);
        if (res instanceof Creature) {
          creatures.push(res);
          creature = res;
        }
      }
      if (creature) {
        for (const fb of creature.fbs) {
          for (const f of fb.features) {
            await this.connectCreatureAction(f);
          }
        }
      }
      // if (creature) {
      //   await minion.connectCreature(creature, true);
      // }
    } 

    ci.spirit.ffs = { traits: [], actions: [], legendary_actions: [], lair_actions: [] };
    for (const ff_id of ci.spirit.ff_ids.traits) {
      const ff = api.getCForFF(ff_id);
      if (ff) {
        await api.loadObject(ff);
        ci.spirit.ffs.traits.push(ff);
      }
    }
    for (const ff_id of ci.spirit.ff_ids.actions) {
      const ff = api.getCForFF(ff_id);
      if (ff) {
        await api.loadObject(ff);
        ci.spirit.ffs.actions.push(ff);
      }
    }
    for (const ff_id of ci.spirit.ff_ids.legendary_actions) {
      const ff = api.getCForFF(ff_id);
      if (ff) {
        await api.loadObject(ff);
        ci.spirit.ffs.legendary_actions.push(ff);
      }
    }
    for (const ff_id of ci.spirit.ff_ids.lair_actions) {
      const ff = api.getCForFF(ff_id);
      if (ff) {
        await api.loadObject(ff);
        ci.spirit.ffs.lair_actions.push(ff);
      }
    }
    if (ci.spirit.inherited_spells.length > 0 && char) {
      ci.spirit.char_spells = [];
      for (const i_spell of ci.spirit.inherited_spells) {
        const cfs = char.spirit.char_feature_sources.find(o => o.true_id === i_spell.source_true_id);
        if (cfs) {
          const char_spell = cfs.spellcasting.spells.find(o => o.true_id === i_spell.true_id);
          if (char_spell) {
            ci.spirit.char_spells.push(char_spell);
          }
        }
      }
    }
    // CreatureGifts
    const remove_us: string[] = [];
    if (ci.spirit.creature_gifts.length > 0) {
      const gifts = await data_util.getCreatureGifts();
      for (const char_gift of ci.spirit.creature_gifts) {
        const gift = gifts.find(o => o._id === char_gift.source_id);
        if (gift) {
          await data_util.loadObject(gift);
          await char_gift.copy_source({ char: ci, feature_source: gift });
          char_gift.table = await data_util.getTableAsync(gift); 
          for (const cfb of char_gift.all_char_feature_bases) {
            await this.connectFeatureBase(cfb, ci);
          }
        } else {
          remove_us.push(char_gift.source_id);
        }
      }
    }
    if (char) {
      ci.subscription_holder.feedTo = char.subscription_holder;
    }
  }
  connectCreatureAction = async (action: Feature) => {
    if (action.floating_feature instanceof CreatureSpellAsAbility) {
      const csaf = action.floating_feature;
      if (!csaf.spell) {
        const spell = data_util.spells.filter(o => o._id === csaf.spell_id)[0];
        if (spell) {
          await data_util.loadObject(spell);
          csaf.connectSpell(spell);
        }
      }
    }
  }
  connectFeatureBase = async (cfb: CharacterFeatureBase, char: CharacterSheetInterface) => {
    for (let j = 0; j < cfb.features.length; ++j) {
      const cf = cfb.features[j];
      if (cf.feature_type === "Feat") {
        let maybe_opt: CharacterFeat | null = null;
        if (cf.feature_options[0] instanceof CharacterFeat) {
          maybe_opt = cf.feature_options[0];
        } else {
          cf.feature_options[0] = new CharacterFeat(cf.feature_options[0]);
          maybe_opt = cf.feature_options[0];
        }
        if (maybe_opt && maybe_opt.source_id !== "") {
          const opt = maybe_opt;
          // There's a mismatch between the sub_cfs on background and the opt.
          // The opt should be the real one though, 
          // but it seems like it's getting stuck in the past 
          // instead of getting updated and used as the source of truth.
          const feat = data_util.feats.find(o => o._id === opt.source_id);
          if (feat) {
            await data_util.loadObject(feat);
            if (opt && opt.copy_source) {
              await opt.copy_source({ char, feature_source: feat, item_id: cf.use_item_id });
            } else {
              debugger;
            }
          }
          if (opt.feat_source_id === "") {
            opt.feat_source_id = opt.root_id;
            opt.feat_source_name = opt.root_name;
            opt.feat_source_type = opt.root_type;
          }
        }
      } else if (cf.feature_type === "Pact Boon") {
        const opt = cf.feature_options[0] as CharacterPactBoon;
        if (opt.source_id !== "") {
          const pact_boons = await data_util.getPactBoons();
          const pact_boon = pact_boons.find(o => o._id === opt.source_id);
          if (pact_boon) {
            await data_util.loadObject(pact_boon);
            await opt.copy_source({ char, feature_source: pact_boon, parent_cf: cf, item_id: cf.use_item_id });
          }
        }
      } else if (cf.feature_type === "Eldritch Invocations" || 
        cf.feature_type === "Fighting Style") {
      } else if (cf.feature_type === "Weapon Mastery") {
        // Handled elsewhere
      } else if (cf.feature_type.includes("Special Feature") && cf.feature_type !== "Special Feature List Merge") {
        for (let i = 0; i < cf.feature_options.length; ++i) {
          if (!(cf.feature_options[i] instanceof CharacterSpecialFeature)) {
            cf.feature_options[i] = new CharacterSpecialFeature(cf.feature_options[i]);
          }
          const opt = cf.feature_options[i];
          if (cf.feature_options[i] instanceof CharacterSpecialFeature) {
            if (opt.source_id !== "") {
              const sf = data_util.special_features.find(o => o._id === opt.source_id);
              if (sf) {
                await data_util.loadObject(sf);
                await opt.copy_source({ char, feature_source: sf, parent_cf: cf, item_id: cf.use_item_id });
              }
            }
          }
        }
      } else {
      }
    }
  }
  connectMagicItemKeyword = async ({
    char, 
    char_item_or_id, 
    magic_item_keyword_or_id,
    source_id = "",
    char_feature_id = ""
  }: {
    char: Character | Campaign, 
    char_item_or_id: CharacterItem | string, 
    magic_item_keyword_or_id: MagicItemKeyword | string, 
    source_id?: string,
    char_feature_id?: string
  }) => {
    let char_item: CharacterItem | null = null;
    let magic_item_keyword: MagicItemKeyword | null = null;
    if (char_item_or_id instanceof CharacterItem) {
      char_item = char_item_or_id;
    } else {
      const id = char_item_or_id;
      const item_finder = char.inventory.items.find(o => o.true_id === id);
      if (item_finder) {
        char_item = item_finder;
      }
    }
    if (magic_item_keyword_or_id instanceof MagicItemKeyword) {
      magic_item_keyword = magic_item_keyword_or_id;
    } else {
      const id = magic_item_keyword_or_id;
      const keyword_finder = data_util.magic_item_keywords.find(o => o._id === id);
      if (keyword_finder) {
        magic_item_keyword = keyword_finder;
      }
    }
    if (char_item && magic_item_keyword) {
      const extra = char_item.extra;
      let extra_spell: Spell | null = null;
      const spell = data_util.spells.find(o => o._id === extra);
      if (spell) {
        await data_util.loadObject(spell);
        extra_spell = spell;
      }
      // debugger;
      await char_item.connectMagicItemKeyword({ 
        owner: char, 
        keyword: magic_item_keyword, 
        class_id: source_id,
        extra_spell,
        char_feature_id
      });
    }
  }
  
  addFeat = async (char: Character, feat: Feat) => {
    const char_feat = new CharacterFeat();
    await data_util.loadObject(feat);
    await char_feat.copy_source({ char, feature_source: feat });
    char.spirit.extra_feats.push(char_feat);
    await this.connectCharacter(char);
    char.changed();
    return char_feat;
  }
  removeFeat = async (char: Character, feat: CharacterFeat) => {
    char.spirit.extra_feats = char.spirit.extra_feats.filter(o => o.true_id !== feat.true_id);
    char.changed();
    
    await this.connectCharacter(char);
  }
  addGift = async (char: Character, gift: Gift) => {
    await data_util.loadObject(gift);
    const char_gift = new CharacterGift();
    await char_gift.copy_source({ char, feature_source: gift });
    char.spirit.gifts.push(char_gift);
    await this.connectCharacter(char);
    char.changed();
    
    return char_gift;
  }
  removeGift = async (char: Character, gift: CharacterGift) => {
    char.spirit.gifts = char.spirit.gifts.filter(o => o.source_id !== gift.source_id);
    char.changed();
    
    await this.connectCharacter(char);
  }
  addBastionFacility = async (char: Character, bf: BastionFacility) => {
    const char_bf = new CharacterBastionFacility();
    char_bf.source = bf;
    char_bf.source_id = bf._id;
    char.spirit.bastion_facilities.push(char_bf);
    await this.connectCharacter(char);
    char.changed();
    
    char.subscription_holder.bastionChanged();
    return char_bf;
  }
  deleteBastionFacility = async (char: Character, char_bf: CharacterBastionFacility) => {
    char.spirit.bastion_facilities = char.spirit.bastion_facilities.filter(o => o.true_id !== char_bf.true_id);
    await this.connectCharacter(char);
    char.changed();
    
    char.subscription_holder.bastionChanged();
  }
  addClass = async (char: Character, game_class: GameClass) => {
    await data_util.loadObject(game_class);
    if (char.spirit.classes.length === 1 && char.mind.character_level < 1) {
      char.spirit.classes[0].level = 1;
      char.spirit.classes[0].do_suggestions = true;
    }
    const char_class = new CharacterClass();
    char_class.position = char.spirit.classes.length;
    await char_class.copy_source({ char, feature_source: game_class });
    char_class.level = 1;
    char_class.do_suggestions = true;
    char.spirit.classes.push(char_class);
    for (let i = 0; i < char_class.all_char_feature_bases.length; ++i) {
      await this.connectFeatureBase(char_class.all_char_feature_bases[i], char);
    }
    await this.connectCharacter(char);
    char.changed();
    
    return char_class;
  }
  removeClass = async (char: Character, char_class: CharacterClass) => {
    char.spirit.classes = char.spirit.classes.filter(o => o.source_id !== char_class.source_id);
    char.spirit.classes.filter(o => o.position > char_class.position).forEach(cc => {
      cc.position--;
      if (cc.position === 0) {
        cc.fixWithGameClass(char);
      }
    });
    char.mind.spells = char.mind.spells.filter(o => o.source_type !== "Class" || o.source_id !== char_class.source_id);
    char.mind.char_feature_bases = char.mind.char_feature_bases.filter(o => 
      o.source_true_id !== char_class.true_id);
    char.spirit.classes = char.spirit.classes.filter(o => o.source_id !== char_class.source_id);
    char.changed();
    
    await this.connectCharacter(char);
  }
  addBackground = async (char: Character, background: Background | null) => {
    const char_bg = new CharacterBackground();
    char_bg.source_id = background ? background._id : "";
    if (background) {
      await data_util.loadObject(background);
      await char_bg.copy_source({ char, feature_source: background });
      for (let i = 0; i < char_bg.all_char_feature_bases.length; ++i) {
        await this.connectFeatureBase(char_bg.all_char_feature_bases[i], char);
      }
    }
    char.spirit.background = char_bg;
    char.changed();
    await this.connectCharacter(char);
  }
  removeBackground = async (char: Character, char_bg: CharacterBackground) => {
    char.mind.char_feature_bases = char.mind.char_feature_bases.filter(o => 
      o.source_true_id !== char_bg.true_id && 
      o.attached_source_true_id !== char_bg.true_id
    );
    char.spirit.background = new CharacterBackground();
    await this.connectCharacter(char);
    char.changed();
  }
  addItem = async ({
    char, 
    item, 
    count = 1,
    char_bf
  }: {
    char: Character | Campaign, 
    item: Item | PseudoItem | CharacterCustomItem, 
    count?: number,
    char_bf?: CharacterBastionFacility
  }) => {
    let remaining = count;
    let backpack_added: CharacterItem | undefined = undefined;
    const use_item = item instanceof PseudoItem && item.base_item ? item.base_item : item;
    if (use_item instanceof Ammunition || use_item instanceof BasicItem || use_item instanceof Weapon) {
      while (remaining > use_item.bundle_size) {
        await this.addItem({ char, item: use_item, count: use_item.bundle_size, char_bf });
        remaining -= use_item.bundle_size;
      }
    }
    if (char.inventory.items.length === 0) {
      const items: CharacterItem[] = [];
      const containers: CharacterContainer[] = [];
      if (char.inventory instanceof CharacterInventory) {
        const equipment = new CharacterContainer();
        equipment.name = "Equipment";
        equipment.true_id = "Equipment";
        items.push(equipment);
        containers.push(equipment);
        const storage = new CharacterContainer();
        storage.name = "Storage";
        storage.true_id = "Storage";
        items.push(storage);
        containers.push(storage);
        char.inventory.items = items;
        if (use_item.name !== "Backpack") {
          const backpack = data_util.base_pseudo_items.find(o => o.name === "Backpack");
          if (backpack) {
            debugger;
            backpack_added = await this.addItem({ char, item: backpack });
          }
        }
      } else {
        const storage = new CharacterContainer();
        storage.name = "Campaign Storage";
        storage.true_id = "Campaign Storage";
        items.push(storage);
        containers.push(storage);
        char.inventory.items = items;
      }
    }
    let char_item: CharacterItem | null = null;
    switch (use_item.item_type) {
      case "Armor":
        char_item = new CharacterArmor();
      break;
      case "Weapon":
        char_item = new CharacterWeapon();
        const char_weapon = new CharacterWeapon();
        char_weapon.count = remaining;
        if (use_item instanceof Weapon) {
          char_weapon.using_attack_types = [...use_item.the_attack_types];
        }
        char_item = char_weapon;
      break;
      case "Ammunition":
        const char_ammo = new CharacterAmmunition();
        char_ammo.count = remaining;
        char_item = char_ammo;
      break;
      case "Container":
        char_item = new CharacterContainer();
        char.inventory.containers.push(char_item as CharacterContainer);
      break;
      case "Custom":
        if (use_item instanceof CharacterCustomItem) {
          char_item = use_item;
        } else {
          const char_cust = new CharacterCustomItem();
          char_cust.count = remaining;
          char_item = char_cust;
        }
      break;
      case "Deck":
        const char_deck = new CharacterDeck();
        char_item = char_deck;
      break;
      case "Gem":
        const char_gem = new CharacterGem();
        if (item instanceof GemItem) {
          if (item.sizes.length === 0) {
            char_gem.cost = item.cost;
            char_gem.weight = item.weight;
          } else {
            char_gem.gem_size_id = item.sizes[0].true_id;
          }
        }
        char_item = char_gem;
      break;
      case "Book":
        const char_book = new CharacterBook();
        char_item = char_book;
      break;
      default:
        const char_basic = new CharacterBasicItem();
        char_basic.count = remaining;
        char_item = char_basic;
      break;
    }
    if (char_item) {
      if (item instanceof Item) {
        await char_item.connectBaseItem({ owner: char, base_item: item });
      } else if (item instanceof PseudoItem) {
        await char_item.copyPseudoItem(char, item);
      }
      if (char_bf) {
        char_bf.items.push(char_item);
      } else {
        let added_to_stack = false;
        if (!(char_item instanceof CharacterContainer)) {
          const base_item_id = char_item.base_item_id;
          let good_containers = char.inventory.containers.filter(o => o.base_item_ids.includes(base_item_id));
          if (good_containers.length === 0) {
            const item_type = char_item.item_type;
            good_containers = char.inventory.containers.filter(o => o.item_types.includes(item_type));
          }
          const default_container_id = char.inventory instanceof CharacterInventory ? "Equipment" : "Campaign Storage";
          const backpack = char.inventory.containers.filter(o => o.name === "Backpack");
          const equipment = char.inventory.containers.filter(o => o.true_id === default_container_id);
          if (good_containers.length > 0) {
            if (isNaN(good_containers[0].content_weight)) {
              good_containers[0].content_weight = 0;
            }
            if (good_containers[0].max_weight === -1 || (good_containers[0].content_weight + char_item.weight) < good_containers[0].max_weight) {
              char_item.in_char_container_id = good_containers[0].true_id;
              good_containers[0].content_weight += char_item.weight;
            } else if (backpack.length > 0 && (backpack[0].max_weight === -1 || (backpack[0].content_weight + char_item.weight) < backpack[0].max_weight)) {
              char_item.in_char_container_id = backpack[0].true_id;
              backpack[0].content_weight += char_item.weight;
            } else {
              char_item.in_char_container_id = default_container_id;
              if (char_item.weight && char_item.weight > 0) {
                equipment[0].content_weight += char_item.weight;
              }
            }
          } else if (!["Weapon","Armor"].includes(char_item.item_type) && 
            !char_item.item_type.includes("Focus") && 
            !char_item.name.includes("Clothes") && 
            backpack.length > 0 && 
            (backpack[0].max_weight === -1 || 
              (backpack[0].content_weight + char_item.weight) < backpack[0].max_weight
            )
          ) {
            char_item.in_char_container_id = backpack[0].true_id;
            backpack[0].content_weight += char_item.weight;
          } else {
            char_item.in_char_container_id = default_container_id;
            if (char_item.weight && char_item.weight > 0) {
              equipment[0].content_weight += char_item.weight;
            }
          }
          if (char_item.stackable) {
            // Find a stack to join it with
            const in_char_container_id = char_item.in_char_container_id;
            const bundle_size = char_item.bundle_size;
            const stack = char.inventory.items.filter(o => 
              o.base_item_id === base_item_id && 
              o.in_char_container_id === in_char_container_id && 
              o.count + remaining <= bundle_size)[0];
            if (stack && (stack instanceof CharacterBasicItem || 
              stack instanceof CharacterAmmunition || 
              stack instanceof CharacterWeapon)) {
              stack.count += remaining;
              char_item = stack;
              added_to_stack = true;
            }
          }
        }
        if (!added_to_stack) {
          char.inventory.items.push(char_item);
        }
      }
      if (item instanceof PseudoItem && item.extra !== "") {
        char_item.extra = item.extra;
        char_item.extra_spell = item.extra_spell;
      }
    }
    await this.connectItem(char, char_item);
    if (backpack_added) {
      const backpacks = char.inventory.items.filter(o => o.name === "Backpack");
      if (backpacks.length > 1) {
        // Extra backpack.  One should be empty.  Delete it.
        for (const bp of backpacks) {
          // Check if it's empty
          if (char.inventory.items.filter(o => o.in_char_container_id === bp.true_id).length === 0) {
            // It's empty.  Delete it.
            this.removeItem(char, bp);
            break;
          }
        }
      }
    }
    char.subscription_holder.itemsChanged();
    char.changed();
    return char_item;
  }
  removeItem(char: Character | Campaign, item: CharacterItem) {
    let doItemsChanged = true;
    if (char instanceof Character) {
      if (item.attuned) {
        this.unattuneItem(char, item);
        doItemsChanged = false;
      }
      if (item.equipped) {
        this.unequipItem(char, item);
        doItemsChanged = false;
      }
    }
    char.inventory.items = char.inventory.items.filter(o => o.true_id !== item.true_id);
    const default_container_id = char.inventory instanceof CharacterInventory ? "Equipment" : "Campaign Storage";
    if (item instanceof CharacterContainer) {
      char.inventory.items.filter(o => o.in_char_container_id === item.true_id).forEach(item2 => {
        item2.in_char_container_id = default_container_id;
      });
    } else {
      if (item.in_char_container_id === default_container_id) {
        if (item.weight && item.weight > 0 && char.inventory instanceof CharacterInventory) {
          char.inventory.equipment_container.content_weight -= item.weight;
        }
      } else {
        const container_finder = char.inventory.containers.filter(o => o.true_id === item.in_char_container_id);
        if (container_finder.length === 1) {
          const container = container_finder[0];
          if (item.weight && item.weight > 0) {
            container.content_weight -= item.weight;
          }
        }
      }
    }
    char.changed();
    if (doItemsChanged) {
      char.subscription_holder.itemsChanged();
    }
  }
  moveItem = async (char: Character | Campaign, inventory: CharacterInventory | CampaignInventory, item: CharacterItem, container_id: string) => {
    let container_finder = item.in_char_container_id === "Equipment" ? [] : inventory.containers.filter(o => o.true_id === item.in_char_container_id);
    if (container_finder.length === 1) {
      const container = container_finder[0];
      if (item.weight && item.weight > 0) {
        container.content_weight -= item.weight;
      }
    } else {
      if (item.weight && item.weight > 0 && inventory instanceof CharacterInventory) {
        inventory.equipment_container.content_weight -= item.weight;
      }
    }
    if (container_id.includes("char_id:")) {
      item.in_char_container_id = "Equipment";
      const receivable = new Receivable();
      receivable.from_id = char instanceof Campaign ? "DM" : char._id;
      receivable.char_id = container_id.replace("char_id:", "");
      receivable.sent_dt = common_util.get_time_number();
      receivable.message = "";
      receivable.origin_id = api.temp_id;
      receivable.type = "Mail";
      const receivable_item = new ReceivableItem();
      receivable_item.type = "Item";
      receivable_item.obj = item;
      receivable.items.push(receivable_item);
      // Remove it from inventory.
      if (item instanceof CharacterContainer) {
        // They're sending a container.  Send all the items in it.
        const contained_items = inventory.items.filter(o => o.in_char_container_id === item.true_id);
        for (const contained_item of contained_items) {
          const receivable_item = new ReceivableItem();
          receivable_item.type = "Item";
          receivable_item.obj = contained_item;
          receivable.items.push(receivable_item);
        }
        inventory.items = inventory.items.filter(o => o.in_char_container_id !== item.true_id);
      }
      await api.upsertObject("receivable", receivable);
      inventory.items = inventory.items.filter(o => o.true_id !== item.true_id);
    } else if (container_id.includes("Take") && inventory instanceof CampaignInventory && char instanceof Character && char.campaign) {
      const to_inventory = char.inventory;
      item.in_char_container_id = "Equipment";
      to_inventory.items.push(item);
      if (item instanceof CharacterContainer) {
        // They're sending a container.  Send all the items in it.
        const contained_items = inventory.items.filter(o => o.in_char_container_id === item.true_id);
        to_inventory.items.push(...contained_items);
        inventory.items = inventory.items.filter(o => o.in_char_container_id !== item.true_id);
      }
      inventory.items = inventory.items.filter(o => o.true_id !== item.true_id);
      // Need to update the campaign inventory as well.
      await api.upsertObject("campaign_inventory", inventory);

      await this.refreshCampaignInventory(char.campaign, char._id);
    } else if (container_id.includes("Campaign") && inventory instanceof CharacterInventory && char instanceof Character && char.campaign) {
      const to_inventory = char.campaign.inventory;
      item.in_char_container_id = "Campaign Storage";
      to_inventory.items.push(item);
      if (item instanceof CharacterContainer) {
        // They're sending a container.  Send all the items in it.
        const contained_items = inventory.items.filter(o => o.in_char_container_id === item.true_id);
        to_inventory.items.push(...contained_items);
        inventory.items = inventory.items.filter(o => o.in_char_container_id !== item.true_id);
      }
      inventory.items = inventory.items.filter(o => o.true_id !== item.true_id);
      // Need to update the campaign inventory as well.
      await api.upsertObject("campaign_inventory", char.campaign.inventory);
      await this.refreshCampaignInventory(char.campaign, char._id);
    } else {
      item.in_char_container_id = container_id;
      container_finder = inventory.containers.filter(o => o.true_id === item.in_char_container_id);
      if (container_finder.length === 1) {
        const container = container_finder[0];
        if (item.weight && item.weight > 0) {
          container.content_weight += item.weight;
        }
      } else {
        if (item.weight && item.weight > 0 && inventory instanceof CharacterInventory) {
          inventory.equipment_container.content_weight += item.weight;
        }
      }
    }
    char.changed();
    char.subscription_holder.itemsChanged();
  }
  refreshCampaignInventory = async (campaign: Campaign, from_id: string) => {
    const receivable = new Receivable();
    receivable.from_id = from_id;
    receivable.campaign_id = campaign._id;
    receivable.sent_dt = common_util.get_time_number();
    receivable.message = "Refresh Campaign Inventory";
    receivable.temp = true;
    receivable.origin_id = api.temp_id;
    await api.upsertObject("receivable", receivable);
  }
  equipItem(char: Character, item: CharacterItem): void {
    if (item.base_item && item.base_item.item_type === "Armor" && item.base_item instanceof Armor) {
      if (item.base_item.armor_type_name === "Shield") {
        if (!char.inventory.shield_carried) {
          item.equipped = true;
          char.inventory.equipped_items.push(item);
        }
      } else if (!char.inventory.armor_worn) {
        item.equipped = true;
        char.inventory.equipped_items.push(item);
      }
    } else {
      item.equipped = true;
      char.inventory.equipped_items.push(item);
    }
    char.changed();
    char.subscription_holder.itemsChanged();
    char.subscription_holder.recalculate();
  }
  unequipItem(char: Character, item: CharacterItem): void {
    if (item.base_item && item.base_item.item_type === "Armor" && item.base_item instanceof Armor) {
      if (item.base_item.armor_type_name === "Shield") {
        item.equipped = false;
      } else {
        console.log('I think something isnt quite right here.');
        console.log("It's not always setting armor_worn to false when it should");
        item.equipped = false;
      }
    } else {
      item.equipped = false;
    }
    char.changed();
    char.subscription_holder.itemsChanged();
    char.subscription_holder.recalculate();
  }
  attuneItem(char: Character, item: CharacterItem): void {
    if (char.inventory.attuned_items.length < char.mind.max_attuned_items) {
      item.attuned = true;
      char.changed();
      char.subscription_holder.itemsChanged();
      char.subscription_holder.recalculate();
    }
  }
  unattuneItem(char: Character, item: CharacterItem): void {
    item.attuned = false;
    char.changed();
    char.subscription_holder.itemsChanged();
    char.subscription_holder.recalculate();
  }
  addSpell = async (char: Character, spell: Spell | CharacterSpell, 
    source: CharacterFeatureSource, extra = false) => {
    const char_spell = spell instanceof Spell ? new CharacterSpell() : spell;
    if (spell instanceof Spell) {
      await data_util.loadObject(spell);
      char_spell.copySpell(spell);
      char_spell.connectSource(source);
    }
    char.mind.spells.push(char_spell);
    
    if (extra) {
      source.spellcasting.extra_spells.push(char_spell);
    } else {
      source.spellcasting.spells.push(char_spell);
    }
    char.changed();
    // await this.doRecalcs2(char, char);
    // await this.recalcSpells(char);
    char.subscription_holder.spellsChanged();
  }
  removeSpell = async (char: Character, char_spell: CharacterSpell, source_type: string, source_id: string) => {
    if (source_type === "Class") {
      const cfs = char.spirit.char_feature_sources.find(o => o.source_id === source_id);
      if (cfs) {
        cfs.spellcasting.spells = cfs.spellcasting.spells.filter(o => o.true_id !== char_spell.true_id);
        cfs.spellcasting.extra_spells = cfs.spellcasting.extra_spells.filter(o => o.true_id !== char_spell.true_id);
      }
    }
    char.mind.spells = char.mind.spells.filter(o => o.true_id !== char_spell.true_id);
    char.changed();
    // await this.doRecalcs2(char, char);
    // await this.recalcSpells(char);
    char.subscription_holder.spellsChanged();
  }
  addSpellToBook = async (char: Character, spell: Spell | CharacterSpell, 
    source: CharacterFeatureSource, extra: boolean) => {
    if (source.spellcasting.spellbook) {
      const char_spellbook = source.spellcasting.spellbook;
      const char_spell = spell instanceof Spell ? new CharacterSpell() : spell;
      if (spell instanceof Spell) {
        await data_util.loadObject(spell);
        char_spell.copySpell(spell);
        char_spell.connectSource(source);
      }
      char_spell.prepared = false;
      
      if (extra) {
        char_spell.extra = true;
      } else {
        char_spell.extra = false;
      }
      char_spellbook.spells.push(char_spell);
    }
    char.changed();
    // await this.doRecalcs2(char, char);
    // await this.recalcSpells(char);
    char.subscription_holder.spellsChanged();
  }
  removeSpellFromBook = async (char: Character, char_spell: CharacterSpell,
    source: CharacterFeatureSource) => {
    if (source.spellcasting.spellbook) {
      source.spellcasting.spellbook.spells = source.spellcasting.spellbook.spells.filter(o => o.true_id !== char_spell.true_id);
    }
    char.changed();
    // await this.doRecalcs2(char, char);
    // await this.recalcSpells(char);
    char.subscription_holder.spellsChanged();
  }
  connectCampaign2(char: Character, campaign: Campaign) {
    // char.old_campaign_id = campaign._id;
    if (char.c_char && !char.c_char.campaign && char.c_char.campaign_id === campaign._id) {
      char.c_char.campaign = campaign;
    }
  }
}
